import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Badge,
  Tabs,
  Tab,
  Skeleton,
  Button,
  Divider,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import {
  OverdueLoanAlert,
  PendingApprovalAlert,
} from '../../../api/types/dashboard.types';

interface AlertsPanelProps {
  overdueAlerts?: OverdueLoanAlert[];
  pendingApprovals?: PendingApprovalAlert[];
  isLoading?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const AlertsPanel: React.FC<AlertsPanelProps> = ({
  overdueAlerts = [],
  pendingApprovals = [],
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" width="100%" height={48} sx={{ mb: 2 }} />
          {[1, 2, 3].map((i) => (
            <Box key={i} sx={{ mb: 2 }}>
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }

  const renderOverdueAlerts = () => (
    <List>
      {overdueAlerts.length === 0 ? (
        <ListItem>
          <ListItemText
            primary="No overdue loans"
            secondary="All loans are up to date"
          />
        </ListItem>
      ) : (
        overdueAlerts.slice(0, 5).map((alert) => (
          <React.Fragment key={alert.loanId}>
            <ListItem
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
                borderLeft: '4px solid',
                borderLeftColor: alert.daysOverdue > 30 ? 'error.main' : 'warning.main',
              }}
              onClick={() => navigate(`/loans/active?loanId=${alert.loanId}`)}
            >
              <ListItemIcon>
                <WarningIcon
                  color={alert.daysOverdue > 30 ? 'error' : 'warning'}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={500}>
                      {alert.customerName}
                    </Typography>
                    <Chip
                      label={`${alert.daysOverdue} days`}
                      size="small"
                      color={alert.daysOverdue > 30 ? 'error' : 'warning'}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Outstanding: {formatCurrency(alert.outstandingBalance)}
                    </Typography>
                    <Typography variant="caption" color="error.main">
                      Overdue Amount: {formatCurrency(alert.amount)}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider variant="inset" component="li" />
          </React.Fragment>
        ))
      )}
    </List>
  );

  const renderPendingApprovals = () => (
    <List>
      {pendingApprovals.length === 0 ? (
        <ListItem>
          <ListItemText
            primary="No pending approvals"
            secondary="All applications have been reviewed"
          />
        </ListItem>
      ) : (
        pendingApprovals.slice(0, 5).map((approval) => (
          <React.Fragment key={approval.applicationId}>
            <ListItem
              sx={{
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
                borderLeft: '4px solid',
                borderLeftColor: approval.daysPending > 7 ? 'error.main' : 'info.main',
              }}
              onClick={() =>
                navigate(`/loans/applications?appId=${approval.applicationId}`)
              }
            >
              <ListItemIcon>
                <ScheduleIcon
                  color={approval.daysPending > 7 ? 'error' : 'info'}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" fontWeight={500}>
                      {approval.customerName}
                    </Typography>
                    <Chip
                      label={`${approval.daysPending} days`}
                      size="small"
                      color={approval.daysPending > 7 ? 'error' : 'info'}
                    />
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Amount: {formatCurrency(approval.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Applied: {format(new Date(approval.appliedDate), 'MMM dd, yyyy')}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
            <Divider variant="inset" component="li" />
          </React.Fragment>
        ))
      )}
    </List>
  );

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          Alerts & Notifications
        </Typography>

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab
            label={
              <Badge badgeContent={overdueAlerts.length} color="error" max={99}>
                <span style={{ marginRight: overdueAlerts.length > 0 ? 16 : 0 }}>
                  Overdue Loans
                </span>
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={pendingApprovals.length} color="warning" max={99}>
                <span style={{ marginRight: pendingApprovals.length > 0 ? 16 : 0 }}>
                  Pending Approvals
                </span>
              </Badge>
            }
          />
        </Tabs>

        <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
          {activeTab === 0 && renderOverdueAlerts()}
          {activeTab === 1 && renderPendingApprovals()}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={() => {
              if (activeTab === 0) navigate('/loans/active?filter=overdue');
              else navigate('/loans/applications?filter=pending');
            }}
          >
            View All
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default AlertsPanel;
